/**
 * 🦴 MOB DROPS (Crafting Materials)
 *
 * All materials that drop from dungeon mobs and bosses.
 * Organized by rarity tier.
 */

import { ITEM_CATEGORIES } from './consumables';

// ============================================================
// COMMON MATERIALS
// ============================================================
export const COMMON_MATERIALS = {
  bone: {
    id: 'bone',
    name: 'Bone',
    icon: '🦴',
    category: ITEM_CATEGORIES.MATERIAL,
    rarity: 'common',
    description: 'Remains of defeated enemies. Used for basic crafting.',
    stackSize: 999,
    usable: false,
  },
  leather: {
    id: 'leather',
    name: 'Leather',
    icon: '🧳',
    category: ITEM_CATEGORIES.MATERIAL,
    rarity: 'common',
    description: 'Tough hide from dungeon creatures. Used for armor crafting.',
    stackSize: 999,
    usable: false,
  },
  cloth: {
    id: 'cloth',
    name: 'Cloth',
    icon: '🧵',
    category: ITEM_CATEGORIES.MATERIAL,
    rarity: 'common',
    description: 'Tattered fabric from dungeon dwellers. Used for light armor.',
    stackSize: 999,
    usable: false,
  },
  ironOre: {
    id: 'ironOre',
    name: 'Iron Ore',
    icon: '⛏️',
    category: ITEM_CATEGORIES.MATERIAL,
    rarity: 'common',
    description: 'Raw iron found in dungeon walls. Can be smelted into weapons.',
    stackSize: 999,
    usable: false,
  },
  leatherScraps: {
    id: 'leatherScraps',
    name: 'Leather Scraps',
    icon: '🧳',
    category: ITEM_CATEGORIES.MATERIAL,
    rarity: 'common',
    description: 'Small pieces of leather. Can be combined for crafting.',
    stackSize: 999,
    usable: false,
  },
  wolfFang: {
    id: 'wolfFang',
    name: 'Wolf Fang',
    icon: '🦷',
    category: ITEM_CATEGORIES.MATERIAL,
    rarity: 'common',
    description: 'Sharp fang from a dungeon wolf. Used for weapons.',
    stackSize: 999,
    usable: false,
  },
  fur: {
    id: 'fur',
    name: 'Fur',
    icon: '🧶',
    category: ITEM_CATEGORIES.MATERIAL,
    rarity: 'common',
    description: 'Thick fur from dungeon beasts. Used for warm armor.',
    stackSize: 999,
    usable: false,
  },
  rustyScrap: {
    id: 'rustyScrap',
    name: 'Rusty Scrap',
    icon: '🔩',
    category: ITEM_CATEGORIES.MATERIAL,
    rarity: 'common',
    description: 'Old metal fragments. Can be melted down.',
    stackSize: 999,
    usable: false,
  },
  wood: {
    id: 'wood',
    name: 'Wood',
    icon: '🌲',
    category: ITEM_CATEGORIES.MATERIAL,
    rarity: 'common',
    description: 'Wooden debris from dungeon structures. Used for basic crafting.',
    stackSize: 999,
    usable: false,
  },
};

// ============================================================
// UNCOMMON MATERIALS
// ============================================================
export const UNCOMMON_MATERIALS = {
  essence: {
    id: 'essence',
    name: 'Essence',
    icon: '✨',
    category: ITEM_CATEGORIES.MATERIAL,
    rarity: 'uncommon',
    description: 'Magical energy from defeated foes. Used for enchanting.',
    stackSize: 500,
    usable: false,
  },
  crystal: {
    id: 'crystal',
    name: 'Crystal',
    icon: '💎',
    category: ITEM_CATEGORIES.MATERIAL,
    rarity: 'uncommon',
    description: 'Glowing crystal from treasure chests. Used for rare crafting.',
    stackSize: 500,
    usable: false,
  },
  rune: {
    id: 'rune',
    name: 'Rune',
    icon: '🔮',
    category: ITEM_CATEGORIES.MATERIAL,
    rarity: 'uncommon',
    description: 'Ancient rune with mysterious power. Used for equipment upgrades.',
    stackSize: 500,
    usable: false,
  },
  orcTusk: {
    id: 'orcTusk',
    name: 'Orc Tusk',
    icon: '🦷',
    category: ITEM_CATEGORIES.MATERIAL,
    rarity: 'uncommon',
    description: 'Massive tusk from an orc warrior. Used for crafting.',
    stackSize: 500,
    usable: false,
  },
  darkSteel: {
    id: 'darkSteel',
    name: 'Dark Steel',
    icon: '⚫',
    category: ITEM_CATEGORIES.MATERIAL,
    rarity: 'uncommon',
    description: 'Corrupted metal from dark knights. Strong but cursed.',
    stackSize: 500,
    usable: false,
  },
  ectoplasm: {
    id: 'ectoplasm',
    name: 'Ectoplasm',
    icon: '👻',
    category: ITEM_CATEGORIES.MATERIAL,
    rarity: 'uncommon',
    description: 'Ghostly residue from wraiths. Used for ethereal items.',
    stackSize: 500,
    usable: false,
  },
  necroticEssence: {
    id: 'necroticEssence',
    name: 'Necrotic Essence',
    icon: '💀',
    category: ITEM_CATEGORIES.MATERIAL,
    rarity: 'uncommon',
    description: 'Essence of undeath. Used for cursed enchantments.',
    stackSize: 500,
    usable: false,
  },
};

// ============================================================
// RARE MATERIALS
// ============================================================
export const RARE_MATERIALS = {
  soulShard: {
    id: 'soulShard',
    name: 'Soul Shard',
    icon: '💠',
    category: ITEM_CATEGORIES.MATERIAL,
    rarity: 'rare',
    description: 'Fragment of a powerful soul. Used for epic equipment.',
    stackSize: 250,
    usable: false,
  },
  dragonScale: {
    id: 'dragonScale',
    name: 'Dragon Scale',
    icon: '🐉',
    category: ITEM_CATEGORIES.MATERIAL,
    rarity: 'rare',
    description: 'Scale from a legendary beast. Used for legendary armor.',
    stackSize: 250,
    usable: false,
  },
  voidFragment: {
    id: 'voidFragment',
    name: 'Void Fragment',
    icon: '🌑',
    category: ITEM_CATEGORIES.MATERIAL,
    rarity: 'rare',
    description: 'Piece of the void itself. Used for powerful enchantments.',
    stackSize: 250,
    usable: false,
  },
  demonHorn: {
    id: 'demonHorn',
    name: 'Demon Horn',
    icon: '👹',
    category: ITEM_CATEGORIES.MATERIAL,
    rarity: 'rare',
    description: 'Twisted horn from a demon. Radiates dark energy.',
    stackSize: 250,
    usable: false,
  },
  sulfur: {
    id: 'sulfur',
    name: 'Sulfur',
    icon: '🌋',
    category: ITEM_CATEGORIES.MATERIAL,
    rarity: 'rare',
    description: 'Infernal brimstone. Used for fire-based enchantments.',
    stackSize: 250,
    usable: false,
  },
  vampireFang: {
    id: 'vampireFang',
    name: 'Vampire Fang',
    icon: '🧛',
    category: ITEM_CATEGORIES.MATERIAL,
    rarity: 'rare',
    description: 'Fang from a vampire. Drains life force.',
    stackSize: 250,
    usable: false,
  },
  bloodVial: {
    id: 'bloodVial',
    name: 'Blood Vial',
    icon: '🩸',
    category: ITEM_CATEGORIES.MATERIAL,
    rarity: 'rare',
    description: 'Vial of cursed blood. Used for dark rituals.',
    stackSize: 250,
    usable: false,
  },
  ogreHeart: {
    id: 'ogreHeart',
    name: 'Ogre Heart',
    icon: '❤️',
    category: ITEM_CATEGORIES.MATERIAL,
    rarity: 'rare',
    description: 'Massive heart from an ogre chieftain. Still beating.',
    stackSize: 100,
    usable: false,
  },
  thickHide: {
    id: 'thickHide',
    name: 'Thick Hide',
    icon: '🦏',
    category: ITEM_CATEGORIES.MATERIAL,
    rarity: 'rare',
    description: 'Incredibly tough hide from a boss monster.',
    stackSize: 100,
    usable: false,
  },
  lichPhylactery: {
    id: 'lichPhylactery',
    name: 'Lich Phylactery',
    icon: '📿',
    category: ITEM_CATEGORIES.MATERIAL,
    rarity: 'rare',
    description: "Container of a lich's soul. Immensely powerful.",
    stackSize: 50,
    usable: false,
  },
  dragonHeart: {
    id: 'dragonHeart',
    name: 'Dragon Heart',
    icon: '🔥',
    category: ITEM_CATEGORIES.MATERIAL,
    rarity: 'rare',
    description: 'Still-burning heart of an ancient dragon.',
    stackSize: 50,
    usable: false,
  },
  ancientBone: {
    id: 'ancientBone',
    name: 'Ancient Bone',
    icon: '🦴',
    category: ITEM_CATEGORIES.MATERIAL,
    rarity: 'rare',
    description: 'Fossilized bone from primordial times. Incredibly durable.',
    stackSize: 100,
    usable: false,
  },
};

// ============================================================
// EPIC MATERIALS
// ============================================================
export const EPIC_MATERIALS = {
  divineEssence: {
    id: 'divineEssence',
    name: 'Divine Essence',
    icon: '⭐',
    category: ITEM_CATEGORIES.MATERIAL,
    rarity: 'epic',
    description: 'Pure divine energy. Used for godly equipment.',
    stackSize: 100,
    usable: false,
  },
  eldritchHeart: {
    id: 'eldritchHeart',
    name: 'Eldritch Heart',
    icon: '❤️‍🔥',
    category: ITEM_CATEGORIES.MATERIAL,
    rarity: 'epic',
    description: 'Heart of an ancient horror. Used for cursed items.',
    stackSize: 100,
    usable: false,
  },
  voidEssence: {
    id: 'voidEssence',
    name: 'Void Essence',
    icon: '🌌',
    category: ITEM_CATEGORIES.MATERIAL,
    rarity: 'epic',
    description: 'Pure essence of the void. Reality bends around it.',
    stackSize: 100,
    usable: false,
  },
  cosmicFragment: {
    id: 'cosmicFragment',
    name: 'Cosmic Fragment',
    icon: '💫',
    category: ITEM_CATEGORIES.MATERIAL,
    rarity: 'epic',
    description: 'Fragment from the cosmos itself. Contains star matter.',
    stackSize: 100,
    usable: false,
  },
  starMetal: {
    id: 'starMetal',
    name: 'Star Metal',
    icon: '⭐',
    category: ITEM_CATEGORIES.MATERIAL,
    rarity: 'epic',
    description: 'Metal forged in the heart of a dying star.',
    stackSize: 100,
    usable: false,
  },
};

// ============================================================
// LEGENDARY MATERIALS
// ============================================================
export const LEGENDARY_MATERIALS = {
  cosmicOre: {
    id: 'cosmicOre',
    name: 'Cosmic Ore',
    icon: '🌌',
    category: ITEM_CATEGORIES.MATERIAL,
    rarity: 'legendary',
    description: 'Ore from beyond the stars. The rarest material.',
    stackSize: 50,
    usable: false,
  },
  eterniumIngot: {
    id: 'eterniumIngot',
    name: 'Eternium Ingot',
    icon: '🔱',
    category: ITEM_CATEGORIES.MATERIAL,
    rarity: 'legendary',
    description: 'Timeless metal that never dulls. Ultimate crafting material.',
    stackSize: 50,
    usable: false,
  },
};

// ============================================================
// ALL MOB DROPS (Combined)
// ============================================================
export const MOB_DROPS = {
  ...COMMON_MATERIALS,
  ...UNCOMMON_MATERIALS,
  ...RARE_MATERIALS,
  ...EPIC_MATERIALS,
  ...LEGENDARY_MATERIALS,
};

// Backwards compatibility alias
export const DUNGEON_RESOURCES = MOB_DROPS;

// ============================================================
// DROP TABLES BY RARITY
// ============================================================
export const DUNGEON_DROP_TABLES = {
  common: [
    { resource: 'bone', weight: 30, amount: [1, 3] },
    { resource: 'leather', weight: 25, amount: [1, 2] },
    { resource: 'cloth', weight: 25, amount: [1, 2] },
    { resource: 'ironOre', weight: 20, amount: [1, 2] },
  ],
  uncommon: [
    { resource: 'essence', weight: 30, amount: [1, 2] },
    { resource: 'crystal', weight: 25, amount: [1, 2] },
    { resource: 'rune', weight: 25, amount: [1, 1] },
    { resource: 'bone', weight: 10, amount: [3, 5] },
    { resource: 'leather', weight: 10, amount: [2, 4] },
  ],
  rare: [
    { resource: 'soulShard', weight: 30, amount: [1, 1] },
    { resource: 'dragonScale', weight: 25, amount: [1, 1] },
    { resource: 'voidFragment', weight: 20, amount: [1, 1] },
    { resource: 'essence', weight: 15, amount: [2, 4] },
    { resource: 'crystal', weight: 10, amount: [2, 3] },
  ],
  epic: [
    { resource: 'divineEssence', weight: 40, amount: [1, 2] },
    { resource: 'eldritchHeart', weight: 30, amount: [1, 1] },
    { resource: 'soulShard', weight: 20, amount: [2, 3] },
    { resource: 'dragonScale', weight: 10, amount: [1, 2] },
  ],
  legendary: [
    { resource: 'cosmicOre', weight: 45, amount: [1, 1] },
    { resource: 'eterniumIngot', weight: 35, amount: [1, 1] },
    { resource: 'divineEssence', weight: 20, amount: [2, 3] },
  ],
};

// ============================================================
// HELPER FUNCTIONS
// ============================================================

/**
 * Roll for dungeon resource drops
 */
export function rollDungeonLoot(dropTable, seed = Date.now()) {
  const table = DUNGEON_DROP_TABLES[dropTable];
  if (!table) return [];

  let state = seed;
  const rng = () => {
    state = (state * 1664525 + 1013904223) % 4294967296;
    return state / 4294967296;
  };

  const totalWeight = table.reduce((sum, entry) => sum + entry.weight, 0);
  const roll = rng() * totalWeight;

  let cumulative = 0;
  for (const entry of table) {
    cumulative += entry.weight;
    if (roll < cumulative) {
      const [min, max] = entry.amount;
      const amount = Math.floor(min + rng() * (max - min + 1));
      return [{ resourceId: entry.resource, amount }];
    }
  }

  return [];
}

/**
 * Get resource data by ID
 */
export function getDungeonResource(resourceId) {
  return MOB_DROPS[resourceId] || null;
}

/**
 * Get all dungeon resources as array
 */
export function getAllDungeonResources() {
  return Object.values(MOB_DROPS);
}

/**
 * Get dungeon resources by rarity
 */
export function getDungeonResourcesByRarity(rarity) {
  return Object.values(MOB_DROPS).filter(r => r.rarity === rarity);
}
